import {
  AlertCircle,
  BookOpen,
  Building2,
  ChevronRight,
  CreditCard,
  Edit3,
  Mail,
  Percent,
  Plus,
  Search,
  Users
} from 'lucide-react';
import React, { useState } from 'react';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import Modal from '../../../../components/shared/Modal/Modal';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { AUTHOR_STATUS_OPTIONS, LIQUIDATION_PERIOD_LABELS, LIQUIDATION_PERIOD_OPTIONS } from '../../../../constants/authors';
import { MOCK_AUTHORS } from '../../../../mocks/Authors.mocks';
import { MOCK_BOOKS } from '../../../../mocks/Books.mocks';
import { Author } from '../../../../types/Author.types';
import { Book } from '../../../../types/Book.types';
import { useToast } from '../../../../hooks/useToast';
import '../shared-modules.css';
import './AuthorsView.css';

export const AuthorsView = () => {
  const { notify } = useToast();
  const [authors, setAuthors] = useState<Author[]>(MOCK_AUTHORS);
  const [books] = useState<Book[]>(MOCK_BOOKS);
  const [selectedAuthor, setSelectedAuthor] = useState<Author | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPeriod, setFilterPeriod] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [deleteModalAuthor, setDeleteModalAuthor] = useState<Author | null>(null);

  const [formData, setFormData] = useState<Omit<Author, 'id'>>({
    name: '', email: '', phone: '', fiscalName: '', taxId: '', address: '',
    bankIban: '', bankSwift: '', liquidationPeriod: 'semiannually',
    royaltyPercentage: 10, status: 'active', associatedBooks: [], notes: ''
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [selectedBooksInput, setSelectedBooksInput] = useState<string[]>([]);

  const handleCreateNewClick = () => {
    setSelectedAuthor(null);
    setFormData({
      name: '', email: '', phone: '', fiscalName: '', taxId: '', address: '',
      bankIban: '', bankSwift: '', liquidationPeriod: 'semiannually',
      royaltyPercentage: 10, status: 'active', associatedBooks: [], notes: ''
    });
    setSelectedBooksInput([]);
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (author: Author) => {
    setSelectedAuthor(author);
    setFormData({
      name: author.name, email: author.email, phone: author.phone || '',
      fiscalName: author.fiscalName, taxId: author.taxId, address: author.address || '',
      bankIban: author.bankIban, bankSwift: author.bankSwift || '',
      liquidationPeriod: author.liquidationPeriod, royaltyPercentage: author.royaltyPercentage,
      status: author.status, associatedBooks: author.associatedBooks || [], notes: author.notes || ''
    });
    setSelectedBooksInput(author.associatedBooks || []);
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleSaveAuthor = () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.fiscalName.trim() || !formData.taxId.trim() || !formData.bankIban.trim()) {
      setFormError('Los campos Nombre, Email, Nombre Fiscal, CIF/NIF e IBAN son obligatorios.');
      return;
    }
    if (formData.bankIban.replace(/\s/g, '').length < 15) {
      setFormError('El código IBAN introducido no parece válido.');
      return;
    }
    if (formData.royaltyPercentage < 0 || formData.royaltyPercentage > 100) {
      setFormError('El porcentaje de regalías debe estar entre 0% y 100%.');
      return;
    }

    const authorPayload: Author = {
      id: selectedAuthor?.id && isFormOpen && authors.some(a => a.id === selectedAuthor.id)
        ? selectedAuthor.id
        : `AUT-${String(authors.length + 1).padStart(2, '0')}`,
      name: formData.name.trim(), email: formData.email.trim(),
      phone: formData.phone?.trim() || undefined, fiscalName: formData.fiscalName.trim(),
      taxId: formData.taxId.trim(), address: formData.address?.trim() || undefined,
      bankIban: formData.bankIban.replace(/\s/g, '').toUpperCase(),
      bankSwift: formData.bankSwift?.trim().toUpperCase() || undefined,
      liquidationPeriod: formData.liquidationPeriod, royaltyPercentage: Number(formData.royaltyPercentage),
      status: formData.status, associatedBooks: selectedBooksInput,
      notes: formData.notes?.trim() || undefined
    };

    if (selectedAuthor?.id && authors.some(a => a.id === selectedAuthor.id)) {
      setAuthors(prev => prev.map(a => a.id === selectedAuthor.id ? authorPayload : a));
      setSelectedAuthor(authorPayload);
      notify('Autor actualizado', 'La ficha del autor se ha guardado correctamente.', 'info');
    } else {
      setAuthors(prev => [...prev, authorPayload]);
      setSelectedAuthor(authorPayload);
      notify('Autor registrado', 'El nuevo autor se ha dado de alta correctamente.', 'info');
    }
    setIsFormOpen(false);
  };

  const handleDeleteAuthor = (author: Author) => {
    setDeleteModalAuthor(author);
  };

  const confirmDelete = () => {
    if (!deleteModalAuthor) return;
    const remaining = authors.filter(a => a.id !== deleteModalAuthor.id);
    setAuthors(remaining);
    setSelectedAuthor(null);
    setIsFormOpen(false);
    setDeleteModalAuthor(null);
    notify('Autor eliminado', 'El autor se ha dado de baja correctamente.', 'info');
  };

  const handleSelectAuthor = (author: Author) => {
    setIsFormOpen(false);
    setSelectedAuthor(author);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedAuthor(null);
  };

  // Clic fuera cierra la ficha. El formulario no: esta vista no avisa de
  // cambios sin guardar y se perdería lo escrito; se cierra con la X.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedAuthor) && !isFormOpen);

  const isEditingAuthor = Boolean(selectedAuthor && authors.some(a => a.id === selectedAuthor.id));

  const toggleBookAssociation = (title: string) => {
    setSelectedBooksInput(prev =>
      prev.includes(title) ? prev.filter(b => b !== title) : [...prev, title]
    );
  };

  const filteredAuthors = authors.filter(author => {
    const matchesSearch =
      author.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      author.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      author.taxId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      author.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPeriod = filterPeriod === 'all' || author.liquidationPeriod === filterPeriod;
    const matchesStatus = filterStatus === 'all' || author.status === filterStatus;
    return matchesSearch && matchesPeriod && matchesStatus;
  });

  const columns: ColumnDef<Author>[] = [
    {
      id: 'id',
      header: 'AUT_ID',
      render: (author) => <span className="authors-view__list-id">{author.id}</span>
    },
    {
      id: 'name',
      header: 'AUTOR / EMAIL',
      render: (author) => (
        <>
          <div className="authors-view__list-name">{author.name}</div>
          <div className="authors-view__list-email">{author.email}</div>
        </>
      )
    },
    { id: 'taxId', header: 'CIF/NIF' },
    {
      id: 'royalty',
      header: 'ROYALTY',
      align: 'center',
      render: (author) => <span className="authors-view__list-royalty">{author.royaltyPercentage}%</span>
    },
    {
      id: 'period',
      header: 'LIQUIDACIÓN',
      render: (author) => (
        <span className="authors-view__list-period">
          {LIQUIDATION_PERIOD_LABELS[author.liquidationPeriod] || author.liquidationPeriod}
        </span>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (author) => (
        <span className={`module-badge ${author.status}`}>
          {author.status === 'active' ? 'ACTIVO' : 'INACTIVO'}
        </span>
      )
    }
  ];

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedAuthor || isFormOpen)} drawer>
        <div className="authors-view">
          <div className="module-header-row">
            <ModuleHeader
              title="Gestión_De_Autores"
              subtitle="Registro de autores, acuerdos de regalías, ciclos de liquidación y control de cuentas bancarias de transferencia."
            />
            <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
              <Plus size={14} />
              Dar_De_Alta_Autor
            </button>
          </div>

          {/* Filter Toolbar */}
          <div className="module-toolbar">
            <div className="module-search-wrapper">
              <Search className="module-search-icon" size={14} />
              <input
                type="text"
                placeholder="FILTRAR POR NOMBRE, EMAIL O CIF..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="module-search-input"
              />
            </div>
            <div className="authors-view__filters">
              <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="module-filter-select">
                {LIQUIDATION_PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="module-filter-select">
                {AUTHOR_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <DataTable
            data={filteredAuthors}
            columns={columns}
            title={<><Users size={14} className="module-panel-title-icon" /> Cartera_De_Autores</>}
            subtitle={`TOTAL: ${filteredAuthors.length}`}
            getRowId={(author) => author.id}
            onRowClick={handleSelectAuthor}
            selectedRowId={selectedAuthor?.id}
            emptyText="No se encontraron autores."
            actionsHeader="ACCIONES"
            actionsColumn={(author) => (
              <RowActionsMenu
                actions={[
                  viewAction(() => handleSelectAuthor(author)),
                  editAction(() => handleEditClick(author)),
                  deleteAction(() => handleDeleteAuthor(author), { label: 'Dar de baja' })
                ]}
              />
            )}
          />
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={isEditingAuthor ? 'Modificar_Ficha_De_Autor' : 'Alta_De_Nuevo_Autor'}
            onClose={closePanel}
            submitLabel="Guardar_Autor"
            onSubmit={handleSaveAuthor}
            showDelete={isEditingAuthor}
            onDelete={() => selectedAuthor && handleDeleteAuthor(selectedAuthor)}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection icon={Mail} title="1. Identificación y Contacto">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Nombre Completo / Firma Literaria *</label>
                <input type="text" required value={formData.name} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, name: val })); }} placeholder="Ej. Gabriel García Márquez" className="sidebar-form-input" />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Correo Electrónico *</label>
                  <input type="email" required value={formData.email} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, email: val })); }} placeholder="autor@ejemplo.com" className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono de Contacto</label>
                  <input type="text" value={formData.phone} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, phone: val })); }} placeholder="+34 600 000 000" className="sidebar-form-input" />
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado del Autor</label>
                  <select value={formData.status} onChange={(e) => { const val = e.target.value as any; setFormData(prev => ({ ...prev, status: val })); }} className="sidebar-form-input">
                    <option value="active">ACTIVO / VIGENTE</option>
                    <option value="inactive">INACTIVO / NO VIGENTE</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Dirección de Notificación</label>
                  <input type="text" value={formData.address} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, address: val })); }} placeholder="Calle Mayor 45, Madrid" className="sidebar-form-input" />
                </div>
              </div>
            </DetailSection>

            <DetailSection icon={Percent} title="2. Condiciones de Liquidación de Regalías / Derechos">
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Período de Liquidación *</label>
                  <select value={formData.liquidationPeriod} onChange={(e) => { const val = e.target.value as any; setFormData(prev => ({ ...prev, liquidationPeriod: val })); }} className="sidebar-form-input">
                    <option value="monthly">MENSUAL</option>
                    <option value="quarterly">TRIMESTRAL</option>
                    <option value="semiannually">SEMESTRAL</option>
                    <option value="annually">ANUAL</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Porcentaje de Regalías (%) *</label>
                  <input type="number" required min="0" max="100" step="0.1" value={formData.royaltyPercentage} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, royaltyPercentage: val })); }} placeholder="Ej. 10" className="sidebar-form-input" />
                </div>
              </div>
              {/* Associated books checkboxes */}
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Obras / Libros Asociados</label>
                <span className="authors-view__hint">Seleccione los títulos del catálogo en los que este autor tiene autoría:</span>
                <div className="authors-view__book-select-grid">
                  {books.map(book => {
                    const isChecked = selectedBooksInput.includes(book.title);
                    return (
                      <label key={book.id} className={`authors-view__book-checkbox-label ${isChecked ? 'checked' : ''}`}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggleBookAssociation(book.title)} />
                        <span className="authors-view__book-checkbox-text">{book.title} ({book.author})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </DetailSection>

            <DetailSection icon={CreditCard} title="3. Información Fiscal y Datos de Transferencia Bancaria">
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Razón Social / Nombre Fiscal Facturación *</label>
                  <input type="text" required value={formData.fiscalName} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, fiscalName: val })); }} placeholder="Ej. Gabriel García Márquez S.L." className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">NIF / CIF / Identificador Fiscal *</label>
                  <input type="text" required value={formData.taxId} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, taxId: val })); }} placeholder="Ej. ESB12345678" className="sidebar-form-input" />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Código de Cuenta Bancaria (IBAN) *</label>
                <input type="text" required value={formData.bankIban} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, bankIban: val })); }} placeholder="ESXX 0000 0000 0000 0000 0000" className="sidebar-form-input" />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Código SWIFT / BIC</label>
                <input type="text" value={formData.bankSwift} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, bankSwift: val })); }} placeholder="SANTEESMMXXX" className="sidebar-form-input" />
              </div>
            </DetailSection>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Notas Adicionales del Contrato / Observaciones</label>
              <textarea value={formData.notes} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, notes: val })); }} rows={2} placeholder="Indique especificaciones de retención de IRPF, representantes, cláusulas de rescisión..." className="sidebar-form-input" />
            </div>
          </SidebarForm>
        ) : selectedAuthor ? (
          <DetailPanel
            drawer
            title="Ficha_De_Autor"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedAuthor)} />
            }
            cards={
              <>
                <DetailCard icon={Percent} title="Condiciones de Liquidación">
                  <DetailFieldList>
                    <DetailField label="Intervalo de Liquidación">
                      {LIQUIDATION_PERIOD_LABELS[selectedAuthor.liquidationPeriod] || selectedAuthor.liquidationPeriod}
                    </DetailField>
                    <DetailField label="Tasa de Regalías">
                      <span className="detail-field-value highlight">{selectedAuthor.royaltyPercentage}%</span>
                    </DetailField>
                  </DetailFieldList>
                </DetailCard>

                <DetailCard icon={Building2} title="Identificación Fiscal">
                  <DetailFieldList>
                    <DetailField label="Razón Social o Nombre Fiscal">{selectedAuthor.fiscalName}</DetailField>
                    <DetailField label="NIF / CIF / Identificador de Impuestos">{selectedAuthor.taxId}</DetailField>
                    {selectedAuthor.address && (
                      <DetailField label="Domicilio Fiscal de Notificaciones">
                        <span className="detail-field-value normal-case">{selectedAuthor.address}</span>
                      </DetailField>
                    )}
                  </DetailFieldList>
                </DetailCard>

                <DetailCard icon={CreditCard} title="Cuenta de Destino Financiero">
                  <DetailFieldList>
                    <DetailField label="Código Cuenta IBAN">{selectedAuthor.bankIban}</DetailField>
                    {selectedAuthor.bankSwift && (
                      <DetailField label="Código SWIFT / BIC">{selectedAuthor.bankSwift}</DetailField>
                    )}
                    <DetailField label="Método de Cobro por Defecto">Transferencia SEPA Electrónica</DetailField>
                  </DetailFieldList>
                </DetailCard>

                <DetailCard icon={BookOpen} title={`Obras en el Catálogo (${selectedAuthor.associatedBooks?.length || 0})`}>
                  <div className="authors-view__book-list">
                    {!selectedAuthor.associatedBooks || selectedAuthor.associatedBooks.length === 0 ? (
                      <span className="authors-view__no-books">Ningún título del catálogo asociado a esta firma</span>
                    ) : (
                      selectedAuthor.associatedBooks.map((title, i) => (
                        <div key={i} className="authors-view__book-item">
                          <ChevronRight size={10} className="authors-view__book-icon" />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                        </div>
                      ))
                    )}
                  </div>
                </DetailCard>

                {selectedAuthor.notes && (
                  <DetailNotes title="Cláusulas de Acuerdo & Notas Especiales">{selectedAuthor.notes}</DetailNotes>
                )}
              </>
            }
          >
            <DetailIdentity
              icon={Users}
              reference={`EXPEDIENTE DE AUTOR • ${selectedAuthor.id}`}
              name={selectedAuthor.name}
              subtitle={<>EMAIL: <span className="detail-identity-highlight">{selectedAuthor.email}</span></>}
            />
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalAuthor}
        type="urgent"
        title="Dar de baja autor"
        message={`¿Está seguro de que desea dar de baja de forma definitiva al autor "${deleteModalAuthor?.name}"?`}
        confirmText="Dar de baja"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModalAuthor(null)}
      />
    </>
  );
};
